import { useEffect, useState, useCallback } from 'react';
import { AnalysisBoard } from '@/components/AnalysisBoard';
import { MobileAnalysisBoard } from '@/components/MobileAnalysisBoard';
import { ChessAppLayout } from '@/components/ChessAppLayout';
import { ensureStockfishReady } from '@/lib/stockfish-engine';
import { useSEO, generateGameJsonLd, generateSoftwareApplicationJsonLd } from '@/hooks/useSEO';
import { useIsMobile } from '@/hooks/use-mobile';
import { createInitialGameState, validateMove, type GameState, type Position, type PieceType } from '@/lib/chess-engine';
import { useLiveEngineAnalysis } from '@/hooks/useLiveEngineAnalysis';

interface Move {
  from: Position;
  to: Position;
  promotion?: PieceType;
}

export default function Analysis() {
  const isMobile = useIsMobile();
  const [gameHistory, setGameHistory] = useState<GameState[]>([createInitialGameState()]);
  const [currentMoveIndex, setCurrentMoveIndex] = useState(0);
  const [orientation, setOrientation] = useState<'white' | 'black'>('white');
  const [analysisEnabled, setAnalysisEnabled] = useState(true);
  
  const currentGameState = gameHistory[currentMoveIndex];
  const analysisState = useLiveEngineAnalysis(currentGameState, analysisEnabled, currentMoveIndex);
  
  // SEO Configuration
  useSEO({
    title: "Chess Game Analysis - Improve Your Play | ChessHatch",
    description: "Analyze your chess games with powerful Stockfish engine analysis. Learn from mistakes, discover better moves, and improve your understanding of chess positions with ChessHatch's free analysis tool.",
    keywords: "chess analysis, chess engine, game analysis, chess improvement, stockfish analysis, chess positions, free chess analysis, chess move analysis",
    jsonLd: [
      generateSoftwareApplicationJsonLd(
        "ChessHatch Analysis Engine",
        "Powerful chess analysis tool using Stockfish engine to analyze games, positions, and suggest improvements for chess players of all levels."
      ),
      generateGameJsonLd()
    ]
  });

  useEffect(() => {
    ensureStockfishReady().catch(console.warn);
  }, []);

  const handleMove = useCallback((move: Move) => {
    const moveResult = validateMove(currentGameState, move.from, move.to, move.promotion);
    if (moveResult.isLegal && moveResult.newState) {
      const newHistory = gameHistory.slice(0, currentMoveIndex + 1);
      newHistory.push(moveResult.newState);
      setGameHistory(newHistory);
      setCurrentMoveIndex(newHistory.length - 1);
    }
  }, [currentGameState, gameHistory, currentMoveIndex]);

  const goToMove = useCallback((moveIndex: number) => {
    setCurrentMoveIndex(Math.max(0, Math.min(moveIndex, gameHistory.length - 1)));
  }, [gameHistory.length]);

  if (isMobile) {
    return (
      <MobileAnalysisBoard
        gameState={currentGameState}
        gameHistory={gameHistory}
        currentMoveIndex={currentMoveIndex}
        onMove={handleMove}
        onGoToMove={goToMove}
        analysisState={analysisState}
        orientation={orientation}
        onOrientationChange={setOrientation}
        analysisEnabled={analysisEnabled}
        onAnalysisEnabledChange={setAnalysisEnabled}
      />
    );
  }

  return (
    <ChessAppLayout>
      <main className="min-h-screen">
        <header className="sr-only">
          <h1>Chess Game Analysis Tool</h1>
        </header>
        <AnalysisBoard 
          analysisState={analysisState}
          analysisEnabled={analysisEnabled}
          onAnalysisEnabledChange={setAnalysisEnabled}
        />
      </main>
    </ChessAppLayout>
  );
}